import { connect } from 'react-redux';
import { Responsive, WidthProvider } from 'react-grid-layout';
import { timeFormat, timeParse } from 'd3';
import { select, showMaximizedChart, hideMaximizedChart } from '../../actions';
import {
  getFanChartData,
  getRiskReturnChartData,
  getDistributionChartData,
  getStatisticsTableData,
  getCorrelationsChartData,
  getInitialDate,
  getInitialAsset,
  getInitialRiskStatistic,
  getInitialReturnStatistic,
} from './selectors';
import React from 'react';
import { Modal } from 'react-bootstrap';
import FanChart from '../../charts/FanChart';
import RiskReturnChart from '../../charts/RiskReturnChart';
import DistributionChart from '../../charts/DistributionChart';
import StatisticsTable from '../../charts/StatisticsTable';
import CorrelationsChart from '../../charts/CorrelationsChart';

const layouts = {
  lg: [
    { i: 'fan-chart', x: 0, y: 0, w: 6, h: 4, minW: 4, minH: 3 },
    { i: 'risk-return-chart', x: 6, y: 0, w: 6, h: 4, minW: 4, minH: 3 },
    { i: 'distribution-chart', x: 0, y: 4, w: 4, h: 4, minW: 4, minH: 2 },
    { i: 'correlations-chart', x: 4, y: 4, w: 4, h: 4, minW: 4, minH: 2 },
    { i: 'statistics-table', x: 8, y: 4, w: 4, h: 4, minW: 4, minH: 2 },
  ],
  xxs: [
    { i: 'fan-chart', x: 0, y: 0, w: 1, h: 4, static: true },
    { i: 'risk-return-chart', x: 0, y: 4, w: 1, h: 4, static: true },
    { i: 'distribution-chart', x: 0, y: 8, w: 1, h: 4, static: true },
    { i: 'correlations-chart', x: 0, y: 12, w: 1, h: 4, static: true },
    { i: 'statistics-table', x: 0, y: 16, w: 1, h: 4, static: true },
  ],
};

const ReactResponsiveGridLayout = WidthProvider(Responsive);

const Dashboard = (props) => {
  const charts = {
    fanChart: <FanChart
      isLoading={props.isLoading}
      onMaximized={() => props.onMaximized('fanChart')}
      data={props.fanChartData}
      onSelected={props.onSelected}
      title={props.asset}
      assets={props.assetMetaData}
      selected={props.date}
    />,
    riskReturnChart: <RiskReturnChart
      isLoading={props.isLoading}
      onMaximized={() => props.onMaximized('riskReturnChart')}
      data={props.riskReturnChartData}
      onSelected={props.onSelected}
      title="Return vs Risk"
      statistics={props.statisticsMetaData}
      frontier={props.frontier}
      riskStatistic={props.riskStatistic}
      returnStatistic={props.returnStatistic}
      asset={props.asset}
      classes={props.classes}
    />,
    distributionChart: <DistributionChart
      isLoading={props.isLoading}
      onMaximized={() => props.onMaximized('distributionChart')}
      data={props.distributionChartData}
      onSelected={props.onSelected}
      selected={props.asset}
      title={!props.isLoading && `${props.asset} - ${timeFormat('%d %B %Y')(timeParse('%d-%b-%Y')(props.dates.filter(d => d.selected)[0].label))}`}
      statistic={props.statistic}
      assets={props.assetMetaData}
      dates={props.dates}
    />,
    correlationsChart: <CorrelationsChart
      isLoading={props.isLoading}
      onMaximized={() => props.onMaximized('correlationsChart')}
      data={props.correlationsChartData}
      title="Correlations"
      onSelected={props.onSelected}
      selected={props.asset}
    />,
    statisticsTable: <StatisticsTable
      isLoading={props.isLoading}
      onMaximized={() => props.onMaximized('statisticsTable')}
      data={props.statisticsTableData}
      title="Statistics"
      onSelected={props.onSelected}
      statistics={props.statisticsMetaData}
      selected={props.asset}
    />,
  }

  const modalChart = props.showMaximizedChart ? React.cloneElement(
    charts[props.maximizedChart],
    { onMaximized: null, onMinimized: () => props.onMinimized() },
  ) : null;

  return (
    <div>
      <ReactResponsiveGridLayout
        className="layout"
        layouts={layouts}
        breakpoints={{ lg: 800, xxs: 0 }}
        cols={{ lg: 12, xxs: 1 }}
        rowHeight={80}
        measureBeforeMount={true}
      >
        <div key="fan-chart">
          {charts.fanChart}
        </div>
        <div key="risk-return-chart">
          {charts.riskReturnChart}
        </div>
        <div key="distribution-chart">
          {charts.distributionChart}
        </div>
        <div key="correlations-chart">
          {charts.correlationsChart}
        </div>
        <div key="statistics-table">
          {charts.statisticsTable}
        </div>
      </ReactResponsiveGridLayout>
      <div className="maximized-chart">
        <Modal show={props.showMaximizedChart} onHide={props.onMinimized} dialogClassName="maximized-chart-modal">
          {modalChart}
        </Modal>
      </div>
    </div>
  );
}

const mapStateToProps = (state, props) => {
  if (props.isLoading) {
    return {
      isLoading: true,
    };
  }

  const isDataPresent = state.local.esg === undefined;

  const date = isDataPresent || state.local.esg.date === undefined ? getInitialDate(props) : state.local.esg.date;
  const asset = isDataPresent || state.local.esg.asset === undefined ? getInitialAsset(props) : state.local.esg.asset;
  const riskStatistic = isDataPresent || state.local.esg.riskStatistic === undefined ? getInitialRiskStatistic(props) : state.local.esg.riskStatistic;
  const returnStatistic = isDataPresent || state.local.esg.returnStatistic === undefined ? getInitialReturnStatistic(props) : state.local.esg.returnStatistic;
  const classes = isDataPresent || state.local.esg.classes === undefined ? new Set() : state.local.esg.classes;

  const fanChartData = getFanChartData(asset, props);
  const riskReturnChartData = getRiskReturnChartData(date, riskStatistic, returnStatistic, props);
  const distributionChartData = getDistributionChartData(asset, date, returnStatistic, riskStatistic, props);
  const statisticsTableData = getStatisticsTableData(date, props);
  const correlationsChartData = getCorrelationsChartData(date, props);
  const showMaximizedChart = state.local.showMaximizedChart;
  const maximizedChart = state.local.maximizedChart;
  const isLoading = false;

  return {
    isLoading,
    showMaximizedChart,
    maximizedChart,
    asset,
    date,
    classes,
    riskStatistic,
    returnStatistic,
    fanChartData: fanChartData.data,
    assetMetaData: fanChartData.assetMetaData,
    riskReturnChartData: riskReturnChartData.data,
    statisticsMetaData: riskReturnChartData.statisticsMetaData,
    frontier: riskReturnChartData.frontier,
    distributionChartData: distributionChartData.data,
    statistic: distributionChartData.statistic,
    dates: distributionChartData.dates,
    statisticsTableData: statisticsTableData.data,
    correlationsChartData: correlationsChartData.data,
  }
}

const mapDispatchToProps = ({
  onSelected: (key, value) => select(key, value, 'esg'),
  onMaximized: chart => showMaximizedChart(chart),
  onMinimized: hideMaximizedChart,
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Dashboard);



// WEBPACK FOOTER //
// ./src/dashboards/ESG/Dashboard.js